import { useUser } from '@auth0/nextjs-auth0';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import useOutsideElement from '../../hooks/use-outside-element';
import normalize from '../../utils/normalize';
import useLogoUrlReplacement from '../../hooks/use-replacement-of-url';

const Header = (props) => {
  const { title } = props;
  const { user, isLoading } = useUser();
  const router = useRouter();
  const routes = [
    { name: 'Home', pathname: '/home' },
    { name: 'My lobbying material', pathname: '/my-lobbying-material' },
    { name: 'Engagement statistics', pathname: '/engagement-statistics' },
  ];
  const pathnamesConditions = routes.filter((item) => item.pathname === router.pathname)?.length > 0;
  const loginUrl = `${process.env.NEXT_PUBLIC_POLICYMOGUL_BASE_URL}/login`;

  return (
    <>
      {!user && (
        <div className='w-full hidden lg:block bg-purple-100 text-white relative '>
          <div className='container px-5 lg:px-0 flex items-center justify-between'>
            <div className='flex items-center lg:-ml-3'>
              <a
                className='mr-5 hover:bg-white/25 py-[8px] px-3'
                href={`${process.env.NEXT_PUBLIC_POLICYMOGUL_BASE_URL}`}
              >
                PolicyMogul
              </a>
              <Link href={'/'} className=' hover:bg-white/25 py-[8px] px-3'>
                PolicyInfluence
              </Link>
            </div>
            <div className='flex items-center'>
              <a
                className='mr-5  hover:bg-white/25 py-[8px] px-3'
                href={`${process.env.NEXT_PUBLIC_WORDPRESS_SITE}/were-hiring/`}
              >
                We&apos;re hiring
              </a>
              <a
                className='hover:bg-white/25  py-[8px] px-3'
                href={`${process.env.NEXT_PUBLIC_WORDPRESS_SITE}/were-hiring/`}
              >
                Contact us
              </a>
              <a
                href={'/auth/login'}
                className='ml-5 hover:bg-gray-300/10 border border-white rounded-full  px-5 py-[1px] my-[6px]'
                onClick={() => {
                  window.location.assign(loginUrl);
                }}
              >
                Log in
              </a>
            </div>
          </div>
        </div>
      )}

      <header
        className={`${!user || !pathnamesConditions ? 'py-[1.204rem]' : ''} sticky top-0 z-50 bg-white transition shadow-sm`}
      >
        <div className='px-5 container flex justify-end items-center justify-between lg:px-0'>
          <div className={`${!user ? 'hidden' : ''} lg:block lg:w-[230px] py-4 lg:py-0`}>
            <Link href='/' className='inline-block'>
              <img
                className='w-[160px] h-[26px] inline-block'
                src={`${process.env.NEXT_PUBLIC_CDNURL}/images/influence/20221104-policyinfluence-logo.svg`}
                alt='Policy mogul logo'
              />
            </Link>
          </div>
          {title && <p className='pl-5 mx-auto grow max-w-[960px] w-full text-blue-100/70'>{title}</p>}

          {!!user && pathnamesConditions && (
            <nav className='hidden list-none lg:flex gap-10 grow'>
              {routes.map((item) => {
                const { pathname, name } = item;
                return (
                  <li
                    key={`nav-${normalize(name)}`}
                    className={`py-5 ${router.pathname === pathname ? 'nav-active-item' : ''}`}
                  >
                    <Link
                      href={pathname}
                      className={`text-md px-6 py-1 inline-block hover:text-purple-100 rounded-full transition-colors ${router.pathname === pathname ? 'text-purple-100 bg-white' : `hover:bg-white`}`}
                    >
                      {name}
                    </Link>
                  </li>
                );
              })}
            </nav>
          )}

          {isLoading ? (
            <SkeletonButton />
          ) : (
            <>
              {!!user ? (
                <ProfileButton />
              ) : (
                <>
                  <div>
                    <a
                      href={`${process.env.NEXT_PUBLIC_WORDPRESS_SITE}/for-parliamentarians/`}
                      className='hover:bg-gray-300/20 border border-purple-100 rounded-full text-sm px-5 lg:px-7 lg:text-base py-2 text-purple-100'
                    >
                      For parliamentarians
                    </a>
                    <a
                      className='lg:hidden ml-2 lg:ml-4 hover:bg-gray-300/20 border border-purple-100 rounded-full text-sm px-5 lg:px-7 lg:text-base py-2 text-purple-100'
                      href={'/auth/login'}
                    >
                      Organisation login
                    </a>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </header>
    </>
  );
};

const ProfileButton = () => {
  const router = useRouter();
  const { data: activePlan, isLoading } = useQuery(
    ['get-active-plan-profile'],
    async () => {
      return axios.get('/api/get-active-plan');
    },
    {
      retry: false,
    }
  );

  const [isShowSettingsMenu, setIsShowSettingsMenu] = useState(false);
  const { user } = useUser();
  const clearLocalStorage = () => {
    localStorage.removeItem('lobbyingMaterialInProgress');
  };
  const settingsMenuElement = useRef(null);
  const hideSettingsMenu = () => {
    setIsShowSettingsMenu(false);
  };
  useOutsideElement(settingsMenuElement, hideSettingsMenu);
  const pathnamesConditions = router.pathname === '/' || router.pathname === '/my-lobbying-material';

  const { replacementOfUrl } = useLogoUrlReplacement();

  return (
    <div className='relative' ref={settingsMenuElement}>
      {isLoading && <SkeletonButton />}
      {!isLoading && (
        <button
          className={`border border-gray-300 bg-white p-[2px_4px_2px_12px] flex items-center rounded-[20px] cursor-pointer hover:bg-light-blue-6 transition-colors`}
          onClick={() => {
            setIsShowSettingsMenu(!isShowSettingsMenu);
          }}
        >
          <span className='icon-hamburger-menu text-[10px] mr-[9px] text-blue-100' />
          <span
            className='w-[29px] h-[29px] rounded-full bg-no-repeat bg-contain bg-center'
            style={{
              backgroundImage: `url(${replacementOfUrl({ url: activePlan?.data?.organisationLogo ?? user.picture })})`,
            }}
          />
        </button>
      )}
      {isShowSettingsMenu && (
        <nav className='absolute shadow-item rounded-lg bg-white pt-3 pb-2 w-[230px] right-0 top-[40px] text-blue-100/80'>
          <Link
            href={`/settings/account`}
            className='px-5 block py-2 hover:bg-gray-base/30 flex items-center'
            onClick={() => {
              hideSettingsMenu();
            }}
          >
            <span className='icon-suitcase min-w-[20px] mr-3' />
            Account
          </Link>
          <Link
            href={`/my-lobbying-material`}
            className={`px-5 block py-2 hover:bg-gray-base/30 flex items-center ${pathnamesConditions ? 'lg:hidden' : ''}`}
            onClick={() => {
              hideSettingsMenu();
            }}
          >
            <span className='icon-briefing-material min-w-[20px] mr-3' />
            My lobbying material
          </Link>
          <hr className='border-gray-300 border-t my-2' />
          <a
            className='px-5 block py-2 hover:bg-gray-base/30 flex items-center'
            href={'/auth/logout'}
            onClick={() => {
              clearLocalStorage();
            }}
          >
            <span className='icon-logout min-w-[20px] mr-3' />
            Logout
          </a>
        </nav>
      )}
    </div>
  );
};

const SkeletonButton = () => {
  return (
    <SkeletonTheme color='#f2f2f2'>
      <Skeleton height={31} width={70} style={{ borderRadius: '20px' }} />
    </SkeletonTheme>
  );
};

export default Header;
